import { useEffect, useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { uploadFile, absoluteUrl } from "@/lib/uploads";
import { Heart, Lightbulb, Flame, Brain, PartyPopper, ImagePlus, Sparkles, Send, Paperclip, Video as VideoIcon, X as XIcon, Clock, Compass, Zap, Trophy, DollarSign, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import usePresence from "@/lib/usePresence";

const REACTIONS = [
  { id: "love", icon: Heart, color: "#FF6B9D" },
  { id: "insight", icon: Lightbulb, color: "#F5C542" },
  { id: "fire", icon: Flame, color: "#FF8C42" },
  { id: "mind", icon: Brain, color: "#00E5FF" },
  { id: "celebrate", icon: PartyPopper, color: "#7B2CBF" },
];

const STORIES = [
  { name: "Aria", img: "https://images.pexels.com/photos/8107979/pexels-photo-8107979.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Kai", img: "https://images.unsplash.com/photo-1723109540955-f5da6d62111b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxzdHlsaXNoJTIwZnV0dXJpc3RpYyUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MjQxNDQyOHww&ixlib=rb-4.1.0&q=85" },
  { name: "Mira", img: "https://images.pexels.com/photos/8107820/pexels-photo-8107820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

export default function SocialZone() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [reasons, setReasons] = useState({});
  const [feedMode, setFeedMode] = useState("chrono"); // chrono | living
  const [feedLoading, setFeedLoading] = useState(false);
  const [text, setText] = useState("");
  const [enhancing, setEnhancing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [generatingImg, setGeneratingImg] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [pendingVideo, setPendingVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mediaType, setMediaType] = useState("post"); // post | story | reel
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const [livePings, setLivePings] = useState({}); // post_id -> { emotion, key }
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    api.get("/trending/creators")
      .then((r) => setTrending(r.data.creators || []))
      .catch((e) => console.warn("[Social] trending fetch failed", e));
  }, []);

  const onLiveReact = useCallback((data) => {
    if (!data.post_id) return;
    setPosts((ps) => ps.map((p) => p.id === data.post_id
      ? { ...p, reactions: { ...(p.reactions || {}), [data.emotion]: ((p.reactions || {})[data.emotion] || 0) + 1 } }
      : p
    ));
    setLivePings((m) => ({ ...m, [data.post_id]: { emotion: data.emotion, key: Date.now() } }));
  }, []);
  const { sendReact } = usePresence({ onReact: onLiveReact });

  const load = async () => {
    if (feedMode === "living") {
      setFeedLoading(true);
      try {
        const r = await api.post("/feed/personalized", { zone: "social", limit: 20 });
        setPosts(r.data.posts || []);
        setReasons(r.data.reasons || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Living feed failed");
      }
      setFeedLoading(false);
    } else {
      const r = await api.get("/posts", { params: { zone: "social" } });
      setPosts(r.data);
      setReasons({});
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [feedMode]);

  const enhance = async () => {
    if (!text.trim()) return;
    setEnhancing(true);
    try {
      const r = await api.post("/ai/chat", {
        message: `Rewrite this social post in 2-3 sentences, sharper, more compelling, keep the meaning. No hashtags unless natural. POST: "${text}"`,
        provider: "anthropic",
        system: "You are a master social media copywriter. Reply only with the rewritten post.",
      });
      setText(r.data.reply.trim());
      toast.success("Enhanced ✦");
    } catch { toast.error("Enhance failed"); }
    setEnhancing(false);
  };

  const generateImage = async () => {
    if (!text.trim()) {
      toast.error("Write a prompt first");
      return;
    }
    setGeneratingImg(true);
    try {
      const r = await api.post("/ai/image", { prompt: text });
      setPendingImage(r.data.image_url);
      toast.success("Image attached");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image gen failed");
    }
    setGeneratingImg(false);
  };

  const submit = async () => {
    if (!text.trim() && !pendingImage && !pendingVideo) return;
    setPosting(true);
    try {
      await api.post("/posts", {
        content: text,
        image_url: pendingImage,
        video_url: pendingVideo,
        zone: mediaType === "reel" ? "creator" : "social",
        media_type: mediaType,
      });
      setText(""); setPendingImage(null); setPendingVideo(null); setMediaType("post");
      await load();
      toast.success("Posted to the living feed");
    } catch { toast.error("Post failed"); }
    setPosting(false);
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r = await uploadFile(f);
      if (r.content_type.startsWith("video/")) {
        setPendingVideo(r.url);
        if (mediaType === "post") setMediaType("reel");
      } else {
        setPendingImage(r.url);
      }
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    if (videoRef.current) videoRef.current.value = "";
  };

  const react = async (postId, emotion) => {
    try {
      const r = await api.post(`/posts/${postId}/react`, { emotion });
      setPosts((ps) => ps.map((p) => (p.id === postId ? r.data : p)));
      sendReact(postId, emotion);
    } catch (e) {
      console.warn("[SocialZone] react failed", e);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Main feed */}
      <div className="lg:col-span-8 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#00E5FF] mb-1">social layer</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">The Living Feed</h1>
          <p className="text-[#64748B] text-sm mt-1">Evolves with your psychology. Reacts in 5 emotions.</p>
          <div className="mt-4 inline-flex glass rounded-full p-1">
            <button
              data-testid="feed-chrono"
              onClick={() => setFeedMode("chrono")}
              className={`text-xs px-4 py-1.5 rounded-full flex items-center gap-1.5 transition-all ${
                feedMode === "chrono" ? "glass-active text-[#00E5FF]" : "text-[#64748B]"
              }`}
            >
              <Compass size={12} /> Chronological
            </button>
            <button
              data-testid="feed-living"
              onClick={() => setFeedMode("living")}
              className={`text-xs px-4 py-1.5 rounded-full flex items-center gap-1.5 transition-all ${
                feedMode === "living" ? "glass-active text-[#7B2CBF]" : "text-[#64748B]"
              }`}
            >
              <Zap size={12} /> Living (AI)
            </button>
          </div>
        </div>

        {/* Composer */}
        <div className="glass-heavy rounded-3xl p-5">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#7B2CBF] flex items-center justify-center font-bold text-white">
              {user?.name?.[0]?.toUpperCase() || "N"}
            </div>
            <div className="flex-1">
              <textarea
                data-testid="post-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Broadcast a thought into the network…"
                rows={3}
                className="nexus-input resize-none"
              />
              {pendingImage && (
                <div className="mt-3 relative">
                  <img src={absoluteUrl(pendingImage)} alt="ai" className="rounded-xl max-h-64 object-cover w-full" />
                  <button
                    onClick={() => setPendingImage(null)}
                    className="absolute top-2 right-2 text-xs glass px-2 py-1 rounded-full flex items-center gap-1"
                  ><XIcon size={10} /> clear</button>
                </div>
              )}
              {pendingVideo && (
                <div className="mt-3 relative">
                  <video src={absoluteUrl(pendingVideo)} controls className="rounded-xl max-h-72 w-full" />
                  <button
                    onClick={() => setPendingVideo(null)}
                    className="absolute top-2 right-2 text-xs glass px-2 py-1 rounded-full flex items-center gap-1"
                  ><XIcon size={10} /> clear</button>
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <input ref={fileRef} type="file" accept="image/*" data-testid="upload-image" className="hidden" onChange={onPickFile} />
                <input ref={videoRef} type="file" accept="video/*" data-testid="upload-video" className="hidden" onChange={onPickFile} />
                <button data-testid="pick-image" onClick={() => fileRef.current?.click()} disabled={uploading} className="nexus-btn-secondary text-sm flex items-center gap-2 !py-2 !px-3">
                  <Paperclip size={14} /> Image
                </button>
                <button data-testid="pick-video" onClick={() => videoRef.current?.click()} disabled={uploading} className="nexus-btn-secondary text-sm flex items-center gap-2 !py-2 !px-3">
                  <VideoIcon size={14} /> Video
                </button>
                <button
                  data-testid="enhance-btn"
                  onClick={enhance}
                  disabled={enhancing}
                  className="nexus-btn-secondary text-sm flex items-center gap-2 !py-2 !px-3"
                >
                  <Sparkles size={14} /> {enhancing ? "…" : "AI Enhance"}
                </button>
                <button
                  data-testid="ai-image-btn"
                  onClick={generateImage}
                  disabled={generatingImg}
                  className="nexus-btn-secondary text-sm flex items-center gap-2 !py-2 !px-3"
                >
                  <ImagePlus size={14} /> {generatingImg ? "…" : "Generate"}
                </button>
                <select
                  data-testid="media-type-select"
                  value={mediaType}
                  onChange={(e) => setMediaType(e.target.value)}
                  className="bg-white/70 border border-slate-200 rounded-full px-3 py-1.5 text-xs text-[#0F172A] outline-none"
                >
                  <option value="post">Post</option>
                  <option value="story">Story</option>
                  <option value="reel">Reel</option>
                </select>
                <button
                  data-testid="post-submit"
                  onClick={submit}
                  disabled={posting || (!text.trim() && !pendingImage && !pendingVideo)}
                  className="ml-auto nexus-btn-primary disabled:opacity-50 text-sm flex items-center gap-2 !py-2"
                >
                  <Send size={14} /> Post
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Feed */}
        <div className="space-y-4" data-testid="feed-list">
          {feedLoading && (
            <div className="glass rounded-2xl p-8 text-center text-[#64748B]">
              <Sparkles size={20} className="mx-auto mb-2 text-[#7B2CBF] animate-pulse" />
              AI is curating your living feed…
            </div>
          )}
          {!feedLoading && posts.length === 0 && (
            <div className="glass rounded-2xl p-8 text-center text-[#64748B]">
              The feed is awakening. Be the first signal.
            </div>
          )}
          {!feedLoading && posts.map((p, idx) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.04 }}
              className="glass rounded-2xl p-5"
              data-testid={`post-${p.id}`}
            >
              {reasons[p.id] && (
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#7B2CBF] mb-2 flex items-center gap-1">
                  <Zap size={10} /> {reasons[p.id]}
                </div>
              )}
              {p.milestone && (
                <Link
                  to={p.milestone.celebration_url}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] mb-2 hover:opacity-80 transition"
                  style={{ color: p.milestone.kind === "revenue" ? "#00E5FF" : "#F5C542" }}
                  data-testid={`post-milestone-${p.id}`}
                >
                  {p.milestone.kind === "revenue" ? <DollarSign size={10} /> : <Trophy size={10} />}
                  Milestone · {p.milestone.label}
                </Link>
              )}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7B2CBF] to-[#00E5FF] flex items-center justify-center font-bold text-white">
                  {p.author_name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-[#0F172A]">{p.author_name}</div>
                  <div className="text-xs text-[#94A3B8] flex items-center gap-2">
                    {new Date(p.created_at).toLocaleString()}
                    {p.media_type === "story" && <StoryExpiry createdAt={p.created_at} />}
                  </div>
                </div>
              </div>
              <p className="text-[#0F172A] leading-relaxed whitespace-pre-wrap">{p.content}</p>
              {p.image_url && (
                p.milestone ? (
                  <Link to={p.milestone.celebration_url} title={`Open ${p.milestone.label} celebration card`}>
                    <img
                      src={absoluteUrl(p.image_url)}
                      alt={p.milestone.label}
                      className="mt-3 rounded-xl w-full object-cover max-h-96 hover:opacity-95 transition cursor-pointer"
                    />
                  </Link>
                ) : (
                  <img src={absoluteUrl(p.image_url)} alt="" className="mt-3 rounded-xl w-full object-cover max-h-96" />
                )
              )}
              {p.video_url && (
                <video src={absoluteUrl(p.video_url)} controls className="mt-3 rounded-xl w-full max-h-96" />
              )}
              <div className="mt-4 flex items-center gap-2 flex-wrap relative">
                {livePings[p.id] && (
                  <motion.div
                    key={livePings[p.id].key}
                    initial={{ opacity: 1, y: 0, scale: 0.6 }}
                    animate={{ opacity: 0, y: -22, scale: 1.2 }}
                    transition={{ duration: 1.4 }}
                    className="absolute -top-1 left-2 text-xs text-[#F5C542] pointer-events-none font-bold"
                  >+1 {livePings[p.id].emotion}</motion.div>
                )}
                {REACTIONS.map((r) => {
                  const Icon = r.icon;
                  const count = p.reactions?.[r.id] || 0;
                  return (
                    <button
                      key={r.id}
                      data-testid={`react-${r.id}-${p.id}`}
                      onClick={() => react(p.id, r.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full glass hover:scale-105 active:scale-95 transition-all text-sm"
                      style={{ color: r.color }}
                    >
                      <Icon size={14} />
                      <span className="text-[#0F172A]">{count}</span>
                    </button>
                  );
                })}
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* Side rail */}
      <aside className="lg:col-span-4 space-y-6">
        <div className="glass-heavy rounded-3xl p-5">
          <div className="text-xs uppercase tracking-[0.25em] text-[#F5C542] mb-3">Stories</div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {STORIES.map((s) => (
              <div key={s.name} className="flex flex-col items-center gap-1 min-w-[64px]">
                <div className="story-ring">
                  <img src={s.img} alt={s.name} className="relative w-16 h-16 rounded-full border-2 border-white object-cover" />
                </div>
                <div className="text-xs text-[#64748B]">{s.name}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-heavy rounded-3xl p-5">
          <div className="text-xs uppercase tracking-[0.25em] text-[#00E5FF] mb-3">Network Pulse</div>
          <div className="space-y-3">
            {[
              { t: "Posts today", v: posts.length },
              { t: "AI enhancements", v: "Active" },
              { t: "Feed mood", v: "Curious" },
            ].map((m) => (
              <div key={m.t} className="flex items-center justify-between">
                <span className="text-sm text-[#64748B]">{m.t}</span>
                <span className="font-display font-bold text-[#0F172A]">{m.v}</span>
              </div>
            ))}
          </div>
        </div>

        {trending.length > 0 && (
          <div className="glass-heavy rounded-3xl p-5" data-testid="trending-creators">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={14} className="text-[#7B2CBF]" />
              <div className="text-xs uppercase tracking-[0.25em] text-[#7B2CBF]">Trending creators · 7d</div>
            </div>
            <ul className="space-y-2.5">
              {trending.slice(0, 6).map((c, idx) => {
                const Icon = c.latest_kind === "revenue" ? DollarSign : Trophy;
                const accent = c.latest_kind === "revenue" ? "#00E5FF" : "#F5C542";
                return (
                  <li key={c.user_id} data-testid={`trending-creator-${c.user_id}`}>
                    <Link
                      to={`/u/${c.user_id}`}
                      className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-slate-100 transition-all"
                    >
                      <div className="text-xs font-display font-bold text-[#94A3B8] w-4 text-right">{idx + 1}</div>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#7B2CBF] flex items-center justify-center font-display font-black text-white shrink-0">
                        {c.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#0F172A] truncate">{c.name}</div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Icon size={9} style={{ color: accent }} />
                          <span className="text-[10px] text-[#64748B] truncate">{c.latest_label}</span>
                        </div>
                      </div>
                      <div
                        className="text-[10px] font-display font-bold px-1.5 py-0.5 rounded-full"
                        style={{ background: `${accent}22`, color: accent }}
                        title={`${c.milestone_count} milestone${c.milestone_count === 1 ? "" : "s"} in 7 days`}
                      >
                        +{c.milestone_count}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}

function StoryExpiry({ createdAt }) {
  const [text, setText] = useState("");
  useEffect(() => {
    const tick = () => {
      const created = new Date(createdAt);
      const expires = new Date(created.getTime() + 24 * 60 * 60 * 1000);
      const ms = expires - new Date();
      if (ms <= 0) { setText("expired"); return; }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      setText(h > 0 ? `${h}h ${m}m left` : `${m}m left`);
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [createdAt]);
  return (
    <span data-testid="story-expiry" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#F5C542]/15 text-[#F5C542] text-[10px] font-bold">
      <Clock size={9} /> {text}
    </span>
  );
}
